import { FC, ReactNode } from "react";

import { Stack } from "@mui/material";
import { FeatureFlagValue } from "@simplyk/common";

import { useEaseInCents } from "./helpers/useEaseInCents";
import { useRepeatDonorTipLine, useRepeatDonorTipVariant } from "./helpers/useRepeatDonorTipLine";
import { RepeatDonorTipCheckboxLine } from "./RepeatDonorTipCheckboxLine";
import { RepeatDonorTipFeeCoverLine } from "./RepeatDonorTipFeeCoverLine";

interface RepeatDonorTipLineProps {
  tipAmountConfirmation?: ReactNode;
}

export const RepeatDonorTipLine: FC<RepeatDonorTipLineProps> = ({ tipAmountConfirmation }) => {
  const variant = useRepeatDonorTipVariant();
  const {
    blurTip,
    currency,
    defaultTipPercentage,
    editedTipCents,
    editTipAmount,
    formattedAmountWithoutTip,
    hasEditedTip,
    isEditingTip,
    isTipChecked,
    organizationName,
    startEditingTip,
    tip,
    toggleTip,
  } = useRepeatDonorTipLine(variant);
  const isCheckboxVariant = variant === FeatureFlagValue.C || variant === FeatureFlagValue.E;
  const isOrganizationVoice = variant === FeatureFlagValue.E || variant === FeatureFlagValue.F;
  const animatedTip = useEaseInCents(tip, isCheckboxVariant);

  if (!variant) {
    return null;
  }

  if (isCheckboxVariant) {
    return (
      <Stack spacing={1}>
        <RepeatDonorTipCheckboxLine
          checked={isTipChecked}
          onToggle={toggleTip}
          tipAmount={animatedTip}
          tipPercentage={defaultTipPercentage}
          organizationName={organizationName}
          currency={currency}
          isOrganizationVoice={isOrganizationVoice}
          formattedAmountWithoutTip={formattedAmountWithoutTip}
        />
        {tipAmountConfirmation}
      </Stack>
    );
  }

  return (
    <Stack spacing={1}>
      <RepeatDonorTipFeeCoverLine
        tipAmount={tip}
        tipPercentage={defaultTipPercentage}
        organizationName={organizationName}
        currency={currency}
        isEditing={isEditingTip}
        onStartEditing={startEditingTip}
        editedTipCents={editedTipCents}
        onChangeEditedTip={editTipAmount}
        onBlurTip={blurTip}
        hasEditedTip={hasEditedTip}
        isOrganizationVoice={isOrganizationVoice}
        formattedAmountWithoutTip={formattedAmountWithoutTip}
      />
      {tipAmountConfirmation}
    </Stack>
  );
};
